import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, clearAuth, setAuth } from '../lib/api';
import { setLanguage } from '../lib/i18n';
import { isInTelegram, tg } from '../lib/telegram';
import type { Session, User } from '../lib/types';

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  /** The user just deleted their account. */
  | { status: 'deleted' }
  | { status: 'error'; error: unknown }
  | { status: 'authenticated'; user: User; isNewUser: boolean; startParam: string | null };

interface AuthContextValue {
  state: AuthState;
  user: User | null;
  setUser: (user: User) => void;
  retry: () => void;
  /** After the account was deleted: forget the session and show the goodbye screen. */
  accountDeleted: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const DEV_AUTH = import.meta.env.VITE_DEV_AUTH === 'true';

/**
 * In local development (outside Telegram) you can impersonate different
 * users with `?devUser=Lina&devId=2`, which makes testing a shared group easy.
 */
function devIdentity(): { telegramId: number; firstName: string } {
  const params = new URLSearchParams(window.location.search);
  const saved = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('expenditure.devUser') ?? 'null') as {
        telegramId: number;
        firstName: string;
      } | null;
    } catch {
      return null;
    }
  })();
  const identity = {
    telegramId: Number(params.get('devId') ?? saved?.telegramId ?? 10001),
    firstName: params.get('devUser') ?? saved?.firstName ?? 'Dara',
  };
  try {
    sessionStorage.setItem('expenditure.devUser', JSON.stringify(identity));
  } catch {
    // ignore
  }
  return identity;
}

async function createSession(): Promise<Session | null> {
  if (isInTelegram() && tg) return api.loginWithTelegram(tg.initData);
  if (DEV_AUTH) {
    const { telegramId, firstName } = devIdentity();
    return api.loginDev(telegramId, firstName);
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const queryClient = useQueryClient();

  // One login per attempt: StrictMode re-runs effects, and a second login
  // would report isNewUser=false and skip onboarding.
  const inflight = useRef<Promise<Session | null> | null>(null);

  const authenticate = useCallback(async (fresh = false) => {
    setState({ status: 'loading' });
    if (fresh || !inflight.current) inflight.current = createSession();
    try {
      const session = await inflight.current;
      if (!session) {
        setState({ status: 'unauthenticated' });
        return;
      }
      // On a 401 later on, silently re-verify initData and retry once.
      setAuth(session.token, async () => (await createSession().catch(() => null))?.token ?? null);
      setLanguage(session.user.language);
      setState({
        status: 'authenticated',
        user: session.user,
        isNewUser: session.isNewUser,
        startParam: session.startParam,
      });
    } catch (error) {
      inflight.current = null;
      setState({ status: 'error', error });
    }
  }, []);

  useEffect(() => {
    void authenticate();
  }, [authenticate]);

  const setUser = useCallback(
    (user: User) => {
      setLanguage(user.language);
      setState((prev) => (prev.status === 'authenticated' ? { ...prev, user } : prev));
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      user: state.status === 'authenticated' ? state.user : null,
      setUser,
      retry: () => void authenticate(true),
      accountDeleted: () => {
        clearAuth();
        queryClient.clear();
        setState({ status: 'deleted' });
      },
    }),
    [state, setUser, authenticate, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

/** The signed-in user; only call from screens rendered after authentication. */
export function useCurrentUser(): User {
  const { user } = useAuth();
  if (!user) throw new Error('No authenticated user');
  return user;
}
